import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";

import { cn } from "../lib/utils";

const Tabs = TabsPrimitive.Root;

/**
 * Default: compact row (e.g. booking sub-filters).
 * Segmented: full-width hub / detail pages — same look as Calendar hub and property detail.
 * Pill: 21st.dev segmented pill bar with animated sliding indicator.
 */
const tabsListVariants = cva(
  "group relative items-center bg-muted p-1 text-muted-foreground",
  {
    variants: {
      variant: {
        default:
          "inline-flex h-10 justify-center rounded-lg border border-border/40",
        segmented:
          "flex h-auto w-full flex-wrap justify-start gap-1 rounded-lg border border-border/40 bg-muted/70",
        pill:
          "inline-flex h-10 items-center justify-center rounded-xl border border-border/60 bg-muted/80 p-1 text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const TabsList = React.forwardRef(
  ({ className, variant, animated = true, children, ...props }, ref) => {
    const listRef = React.useRef(null);
    const [indicatorStyle, setIndicatorStyle] = React.useState(null);
    const isPill = variant === "pill" || animated;

    const updateIndicator = React.useCallback(() => {
      if (!isPill || !listRef.current) return;

      const activeEl = listRef.current.querySelector('[data-state="active"]');
      if (activeEl) {
        setIndicatorStyle({
          left: activeEl.offsetLeft,
          top: activeEl.offsetTop,
          width: activeEl.offsetWidth,
          height: activeEl.offsetHeight,
        });
      } else {
        setIndicatorStyle(null);
      }
    }, [isPill]);

    React.useEffect(() => {
      if (!isPill || !listRef.current) return;

      updateIndicator();

      // Observe DOM mutations for active tab state changes
      const observer = new MutationObserver(updateIndicator);
      observer.observe(listRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-state"],
      });

      // Observe resizes
      const resizeObserver = new ResizeObserver(updateIndicator);
      resizeObserver.observe(listRef.current);

      return () => {
        observer.disconnect();
        resizeObserver.disconnect();
      };
    }, [isPill, updateIndicator]);

    return (
      <TabsPrimitive.List
        ref={(node) => {
          listRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        data-pill={isPill ? "true" : undefined}
        className={cn(tabsListVariants({ variant }), className)}
        {...props}
      >
        {isPill && indicatorStyle && (
          <span
            className="absolute rounded-md bg-background dark:bg-foreground/[0.12] border border-border/80 shadow-sm transition-all duration-200 ease-out pointer-events-none z-0"
            style={{
              left: `${indicatorStyle.left}px`,
              top: `${indicatorStyle.top}px`,
              width: `${indicatorStyle.width}px`,
              height: `${indicatorStyle.height}px`,
            }}
          />
        )}
        {children}
      </TabsPrimitive.List>
    );
  },
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "relative z-10 inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover:text-foreground text-muted-foreground border border-transparent",
      "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm data-[state=active]:border-border/60 data-[state=active]:font-semibold",
      // When inside animated pill list, let the sliding pill span provide background
      "group-data-[pill=true]:data-[state=active]:bg-transparent group-data-[pill=true]:data-[state=active]:border-transparent group-data-[pill=true]:data-[state=active]:shadow-none",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

/**
 * 21st.dev Segmented Pill Tabs standalone control
 */
function SegmentedPillTabs({
  modes = [],
  value,
  defaultValue,
  onChange,
  onModeChange,
  size = "default",
  customLabels = {},
  className,
}) {
  const [uncontrolledValue, setUncontrolledValue] = React.useState(
    defaultValue || modes[0],
  );
  const containerRef = React.useRef(null);
  const buttonRefs = React.useRef({});
  const [indicatorRect, setIndicatorRect] = React.useState(null);

  const activeValue = value !== undefined ? value : uncontrolledValue;

  const updatePill = React.useCallback(() => {
    const activeBtn = buttonRefs.current[activeValue];
    if (activeBtn) {
      setIndicatorRect({
        left: activeBtn.offsetLeft,
        top: activeBtn.offsetTop,
        width: activeBtn.offsetWidth,
        height: activeBtn.offsetHeight,
      });
    }
  }, [activeValue]);

  React.useEffect(() => {
    updatePill();
  }, [updatePill, modes]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(updatePill);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [updatePill]);

  const handleSelect = (mode) => {
    if (value === undefined) {
      setUncontrolledValue(mode);
    }
    onChange?.(mode);
    onModeChange?.(mode);
  };

  const handleKeyDown = (e) => {
    if (!modes || modes.length === 0) return;
    const currentIndex = modes.indexOf(activeValue);
    let nextIndex = currentIndex;

    if (e.key === "ArrowRight") {
      e.preventDefault();
      nextIndex = currentIndex < modes.length - 1 ? currentIndex + 1 : 0;
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      nextIndex = currentIndex > 0 ? currentIndex - 1 : modes.length - 1;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextIndex = modes.length - 1;
    } else {
      return;
    }

    const nextMode = modes[nextIndex];
    if (nextMode !== undefined) {
      handleSelect(nextMode);
      buttonRefs.current[nextMode]?.focus();
    }
  };

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
      className={cn(
        "relative inline-flex items-center rounded-lg border border-border/60 bg-muted/70 p-1",
        size === "sm" && "h-8",
        size === "default" && "h-10",
        size === "lg" && "h-11",
        className,
      )}
    >
      {/* Single Sliding Active Pill */}
      {indicatorRect && (
        <span
          className="absolute rounded-md bg-background dark:bg-foreground/[0.12] border border-border/80 shadow-sm transition-all duration-200 ease-out pointer-events-none z-0"
          style={{
            left: `${indicatorRect.left}px`,
            top: `${indicatorRect.top}px`,
            width: `${indicatorRect.width}px`,
            height: `${indicatorRect.height}px`,
          }}
        />
      )}

      {/* Tab Mode Buttons */}
      {modes.map((mode) => {
        const isActive = activeValue === mode;
        const label = customLabels[mode] || mode;

        return (
          <button
            key={mode}
            ref={(el) => (buttonRefs.current[mode] = el)}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            data-active={isActive ? "true" : "false"}
            onClick={() => handleSelect(mode)}
            className={cn(
              "relative z-10 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              isActive
                ? "text-foreground font-semibold"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  SegmentedPillTabs,
  tabsListVariants,
};
